import { memo } from 'react';
import { GraduationCap, Award, MapPin } from 'lucide-react';
import useScrollAnimation from '@/hooks/useScrollAnimation';
import { trackLocationClick } from '@/hooks/useGoogleAnalytics';

const credentials = [
  {
    icon: GraduationCap,
    title: 'Nutrición Humana y Dietética',
    institution: 'Universidad Alfonso X el Sabio',
  },
  {
    icon: GraduationCap,
    title: 'Farmacia',
    institution: 'Universidad Francisco de Vitoria',
  },
  {
    icon: GraduationCap,
    title: 'Biotecnología',
    institution: 'Universidad Francisco de Vitoria',
  },
];

const locations = [
  {
    local: 'Farmacia Zazo',
    address: 'Plaza Miguel Echegaray 5',
    city: '28970, Humanes de Madrid',
    mapUrl: 'https://maps.app.goo.gl/FGmYa2ym5KwHNJMt6',
  },
  {
    local: 'Zazo Fitness',
    address: 'Calle Pelayos 3',
    city: '28945, Fuenlabrada',
    mapUrl: 'https://maps.app.goo.gl/3E5aMwDogSwLAnFd7',
  },
];

const About = memo(() => {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation();

  return (
    <section id="sobre-mi" className="section-padding">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <div
            ref={headerRef}
            className={`relative transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            {/* Main image container with glass effect */}
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden glass">
              <div className="absolute inset-0 gradient-sage opacity-30" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center p-8">
                  <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-5xl font-bold text-primary">MG</span>
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground mb-2">
                    Marina González Hurtado
                  </h3>
                  <p className="text-muted-foreground">
                    Nutricionista · Farmacéutica
                  </p>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-rose-light/50 rounded-full blur-xl" />
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-sage-light/50 rounded-full blur-xl" />
            </div>

            {/* Badge */}
            <a 
              href="https://codinma.es/listado-de-colegiados/"
              target="_blank"
              rel="noopener noreferrer"
              className="absolute -bottom-6 left-1/2 -translate-x-1/2 glass-card py-3 px-6 flex items-center gap-3 hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer"
            >
              <Award className="w-6 h-6 text-primary" />
              <div>
                <p className="text-sm font-medium text-foreground">Colegiada</p>
                <p className="text-xs text-muted-foreground hover:text-primary transition-colors">MAD01587</p>
              </div>
            </a>
          </div>

          {/* Content Side */}
          <div
            ref={contentRef}
            className={`transition-all duration-700 delay-200 ${
              contentVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <span className="inline-block text-primary font-medium mb-4">
              Sobre mí
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Nutricionista colegiada{' '}
              <span className="text-gradient">en Madrid</span>
            </h2>
            <p className="text-muted-foreground text-lg mb-4 leading-relaxed">
              Soy Marina González Hurtado, nutricionista y farmacéutica con formación
              en biotecnología. Mi enfoque combina la ciencia de la nutrición con un
              acompañamiento cercano y personalizado. Creo firmemente que cada persona
              es única y merece un plan adaptado a sus necesidades, gustos y estilo de vida.
            </p>
            <p className="text-lg italic text-primary/80 mb-8">
              "La nutrición eficaz empieza cuando se personaliza"
            </p>

            {/* Credentials */}
            <div className="space-y-4 mb-8">
              <h3 className="font-semibold text-foreground mb-4">Formación académica</h3>
              {credentials.map((credential, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 glass-card py-4 hover:shadow-soft-lg"
                >
                  <div className="w-10 h-10 bg-sage-light rounded-lg flex items-center justify-center flex-shrink-0">
                    <credential.icon className="w-5 h-5 text-sage-dark" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{credential.title}</p>
                    <p className="text-sm text-muted-foreground">{credential.institution}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Locations */}
            <div>
              <h3 className="font-semibold text-foreground mb-4">Consultas presenciales</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {locations.map((location, index) => (
                  <a 
                    key={index} 
                    href={location.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="glass-card py-4 flex items-start gap-3 hover:shadow-soft-lg transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                    aria-label={`Ver ${location.local} en Google Maps`}
                    onClick={() => trackLocationClick(location.city, `${location.local} - About`)}
                  >
                    <MapPin className="w-5 h-5 text-rose flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground text-sm">{location.local}</p>
                      <p className="text-sm text-muted-foreground">{location.address}</p>
                      <p className="text-sm text-muted-foreground">{location.city}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

About.displayName = 'About';

export default About;
